/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Base interface for items that contain business line information
 */
interface ItemWithBusinessLine {
  businessLineId: number;
  businessLineName: string;
}

/**
 * Extracts unique business lines from an array of items
 *
 * @param items - Array of items containing business line information
 * @param additionalFields - Optional array of field names to include in the result
 * @returns Array of unique business lines with specified fields
 *
 * @example
 * // Simple usage - only businessLineId and businessLineName
 * const businessLines = extractBusinessLines(items);
 *
 * @example
 * // With additional fields
 * const businessLines = extractBusinessLines(items, ['shippingStartDateTime', 'shippingEndDateTime']);
 * // Result will include: { businessLineId, businessLineName, shippingStartDateTime, shippingEndDateTime }
 */
export function extractBusinessLines<
  T extends ItemWithBusinessLine,
  K extends keyof T = never,
>(
  items: T[] | null | undefined,
  additionalFields?: K[],
): Array<Pick<T, 'businessLineId' | 'businessLineName'> & Partial<Pick<T, K>>> {
  if (!items || items.length === 0) {
    return [];
  }

  type ResultType = Pick<T, 'businessLineId' | 'businessLineName'> &
    Partial<Pick<T, K>>;

  const businessLinesMap = new Map<number, ResultType>();

  items.forEach(item => {
    if (!businessLinesMap.has(item.businessLineId)) {
      const businessLine: any = {
        businessLineId: item.businessLineId,
        businessLineName: item.businessLineName,
      };

      // Add additional fields if specified
      if (additionalFields) {
        additionalFields.forEach(field => {
          businessLine[field] = item[field] ?? undefined;
        });
      }

      businessLinesMap.set(item.businessLineId, businessLine as ResultType);
    }
  });

  return Array.from(businessLinesMap.values());
}
